import axios, { API } from 'lib/config/axios';
import { sleep } from 'views/rebranded-module/common/utils/sleep';
import { EXTERNAL_PAYMENT_STATUS as PAYMENT_STATUS } from 'lib/utils/CONSTANTS';
import { setSnackbar } from 'redux/features/ui-slice';

// Countries where Stripe's billing AddressElement does not render a postal code field.
// Stripe always emits all address keys (line1, city, state, postal_code, country) regardless
// of country, so we cannot infer renderedness from key presence — the hardcoded list is
// the only reliable approach (confirmed with stripe-js 1.42 / react-stripe-js 1.13).
const STRIPE_NO_POSTAL_CODE_COUNTRIES = new Set([
  'AE', 'AG', 'AO', 'AW', 'BB', 'BI', 'BJ', 'BO', 'BS', 'BW', 'BZ',
  'CF', 'CG', 'CI', 'CK', 'CM', 'DJ', 'DM', 'ER', 'FJ', 'GA', 'GD',
  'GH', 'GM', 'GN', 'GQ', 'GW', 'GY', 'HK', 'IQ', 'JM', 'JO', 'KE',
  'KH', 'KI', 'KM', 'KN', 'KW', 'LA', 'LC', 'LR', 'LS', 'MO', 'MR',
  'MS', 'MU', 'MW', 'NA', 'NR', 'NU', 'OM', 'PG', 'QA', 'RW', 'SB',
  'SC', 'SD', 'SL', 'SN', 'SO', 'SR', 'SS', 'ST', 'SX', 'SY', 'SZ',
  'TD', 'TG', 'TK', 'TL', 'TO', 'TT', 'TV', 'TZ', 'UG', 'VC', 'VU',
  'WS', 'YE', 'ZM', 'ZW',
]);

// Countries where Stripe's billing AddressElement renders a state/province selector.
const STRIPE_STATE_COUNTRIES = new Set([
  'US', 'CA', 'AU', 'BR', 'IN', 'MX', 'MY', 'AR',
]);

// Returns an i18n key string if validation fails, null if address is valid.
// Caller supplies the namespace prefix when calling t().
// Postal code is required for all countries that have a postal code system.
// State/province is required only for countries where Stripe renders the selector.
const validateStripeAddress = ({
  stripeAddressValue,
  isStripeSavedCard,
}: {
  stripeAddressValue: any;
  isStripeSavedCard?: boolean;
}) => {
  if (isStripeSavedCard) return null;
  const nameParts = (stripeAddressValue?.name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (nameParts.length < 2) return 'name_required';
  const country = stripeAddressValue?.address?.country;
  if (country && !STRIPE_NO_POSTAL_CODE_COUNTRIES.has(country) && !stripeAddressValue?.address?.postal_code) {
    return 'postal_code_required';
  }
  if (country && STRIPE_STATE_COUNTRIES.has(country) && !stripeAddressValue?.address?.state) {
    return 'state_required';
  }
  if (!stripeAddressValue?.phone) return 'phone_required';
  return null;
};

// Polls payment_result until the webhook transitions MembershipPayment from in_process.
// Calls onSuccess(responseData) on confirmed payment, onFailed() if polling window expires.
const pollMembershipPaymentResult = async ({
  paymentIntentId,
  authenticityToken,
  onSuccess,
  onFailed,
}) => {
  const pollIntervalMs = 1500;
  const maxPollAttempts = 3;
  let attempt = 0;

  while (attempt < maxPollAttempts) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await axios.get(API.stripePayments.paymentResult, {
        params: {
          authenticity_token: authenticityToken,
          payment_intent_id: paymentIntentId,
          payment_type: 'membership',
        },
      });
      if (res.data.status === PAYMENT_STATUS.success) {
        onSuccess(res.data);
        return;
      }
    } catch {
      break;
    }
    attempt++;
    if (attempt >= maxPollAttempts) break;
    // eslint-disable-next-line no-await-in-loop
    await sleep(pollIntervalMs);
  }

  onFailed();
};

// Builds the confirmParams block for stripe.confirmPayment.
// Spread the result directly into the stripe.confirmPayment call alongside `elements`.
const buildConfirmPaymentParams = ({
  stripeAddressValue,
  isStripeSavedCard,
  skipBillingDetails = false,
  currentUser,
  riderData = null,
}) => {
  const resolvedPhone = stripeAddressValue?.phone || riderData?.full_mobile || riderData?.mobile || riderData?.phone || currentUser?.phone || '';
  return {
    redirect: 'if_required',
    confirmParams: {
      return_url: window.location.href,
      ...(!isStripeSavedCard && {
        payment_method_data: {
          billing_details: {
            name:
              stripeAddressValue?.name
              || riderData?.name
              || `${currentUser?.first_name} ${currentUser?.last_name}`.trim()
              || null,
            email: riderData?.email || currentUser?.email || null,
            ...(!skipBillingDetails && {
              phone: resolvedPhone,
              address: {
                line1: stripeAddressValue?.address?.line1 || '',
                line2: stripeAddressValue?.address?.line2 || null,
                city: stripeAddressValue?.address?.city || '',
                state: stripeAddressValue?.address?.state || null,
                postal_code: stripeAddressValue?.address?.postal_code || '',
                country: stripeAddressValue?.address?.country || riderData?.country_iso2 || '',
              },
            }),
          },
        },
      }),
    },
  };
};

const buildCustomerInfo = ({
  stripeAddressValue, name, email, phoneFallback = '', countryFallback = '',
}) => ({
  name,
  email,
  phone: stripeAddressValue?.phone || phoneFallback,
  address: {
    line1: stripeAddressValue?.address?.line1 || '',
    line2: stripeAddressValue?.address?.line2 || '',
    city: stripeAddressValue?.address?.city || '',
    state: stripeAddressValue?.address?.state || '',
    postal_code: stripeAddressValue?.address?.postal_code || '',
    country: stripeAddressValue?.address?.country || countryFallback,
  },
});

// Runs the agent-specific pre-confirm step using handleActions: false to avoid showing
// the 3DS popup. Returns a status the caller uses to decide whether to continue.
// 'requires_action' → show AgentVerificationRequired; 'error' → show errorMessage in snackbar;
// 'ok' → fall through to the backend checkout POST.
const runAgentPreConfirmPayment = async ({
  stripe,
  elements,
  stripeAddressValue,
  userData,
  appState,
  stripeData,
  finalTotalPrice,
  membershipData,
  billingDetailsOverride,
}: {
  stripe: any;
  elements: any;
  stripeAddressValue: any;
  userData: any;
  appState: any;
  stripeData: any;
  finalTotalPrice: any;
  membershipData: any;
  // When address: 'never' is set in PE options (flag OFF), Stripe requires us to pass
  // billing details ourselves. Pass them here so confirmPayment doesn't error.
  billingDetailsOverride?: Record<string, any>;
}): Promise<{ status: 'requires_action' | 'error' | 'ok'; errorMessage?: string }> => {
  const agentConfirmResult = await stripe.confirmPayment({
    elements,
    confirmParams: {
      return_url: window.location.href,
      ...(billingDetailsOverride && {
        payment_method_data: { billing_details: billingDetailsOverride },
      }),
    },
    handleActions: false,
  });

  if (agentConfirmResult.paymentIntent?.status === 'requires_action') {
    return { status: 'requires_action' };
  }

  if (agentConfirmResult.error) {
    return {
      status: 'error',
      errorMessage:
        agentConfirmResult.error.type !== 'validation_error'
          ? agentConfirmResult.error.message
          : undefined,
    };
  }

  return { status: 'ok' };
};

// Builds the metadata block sent with the Stripe PaymentIntent creation request.
const buildMembershipMetadata = ({
  membershipId,
  userId,
  membershipPlan,
  currentUser,
}) => ({
  product: 'ER Membership',
  membership_id: membershipId,
  user_id: userId,
  customer_name: `${currentUser?.first_name || ''} ${
    currentUser?.last_name || ''
  }`.trim(),
  email: currentUser?.email || '',
  membership_plan: membershipPlan || '',
  source_context: 'membership_checkout_page',
});

// Runs address validation then elements.submit() validation.
// Returns true if both pass; dispatches a snackbar and returns false on the first failure.
// translateError maps a key like 'name_required' to a translated string.
const runStripePreSubmitValidation = async ({
  elements,
  stripeAddressValue,
  isStripeSavedCard = false,
  stripeSelectedPaymentType,
  suppressBillingAddress = false,
  translateError,
  dispatch,
}: {
  elements: any;
  stripeAddressValue: any;
  isStripeSavedCard?: boolean;
  stripeSelectedPaymentType?: string | null;
  suppressBillingAddress?: boolean;
  translateError: (key: string) => string;
  dispatch: (action: unknown) => void;
}): Promise<boolean> => {
  // Only card payments use the AddressElement and require billing address validation.
  // All other types (ACH, wallets, BNPL, etc.) either collect billing details natively
  // or don't require them — skip validation and elements.submit() for non-card types.
  if (stripeSelectedPaymentType !== 'card') return true;

  // When billing address is suppressed (no AddressElement shown), skip address validation.
  if (!suppressBillingAddress && stripeAddressValue != null) {
    const addressError = validateStripeAddress({ stripeAddressValue, isStripeSavedCard });
    if (addressError) {
      dispatch(setSnackbar({ open: true, severity: 'error', message: translateError(addressError) }));
      return false;
    }
  }
  const submitResult = await elements.submit();
  if (submitResult.error) {
    if (submitResult.error.message) {
      dispatch(setSnackbar({ open: true, severity: 'error', message: submitResult.error.message }));
    }
    return false;
  }
  return true;
};

// Creates a SetupIntent for 0-dollar checkout flows and returns the stripe data object.
// Callers wrap this with their own loading state and error handler.
const initiateSetupIntentForFreeCheckout = async ({
  authenticityToken,
}: {
  authenticityToken: string;
}) => {
  const res = await axios.post(API.stripePayments.initiateSetupIntent, {
    authenticity_token: authenticityToken,
  });
  return {
    clientKey: res.data.client_key as string,
    clientSecret: res.data.setup_intent_client_secret as string,
    paymentIntentId: null as null,
    isSetupIntent: true as const,
  };
};

// Calls the sync endpoint to reconcile a payment intent whose outcome is unknown —
// used when stripe.confirmPayment was called but we never received webhook confirmation.
// Applies the Stripe status to the DB (same as the webhook handler would) and calls
// onSuccess if the payment is confirmed. Returns true if onSuccess was called so the
// caller can return early, false otherwise.
const syncPaymentIntent = async ({
  reservationId,
  paymentIntentId,
  authenticity_token,
  onSuccess,
}: {
  reservationId: string;
  paymentIntentId: string;
  authenticity_token: string;
  onSuccess: (data?: Record<string, unknown>) => void | Promise<void>;
}): Promise<boolean> => {
  try {
    const res = await axios.post(API.stripePayments.syncPaymentIntentStatus, {
      reservation_id: reservationId,
      payment_intent_id: paymentIntentId,
      authenticity_token,
    });
    if (res.data.status === 'succeeded' || res.data.status === 'processing') {
      await onSuccess(res.data);
      return true;
    }
  } catch {
    // network or server error — let the caller decide what to show
  }
  return false;
};

export {
  validateStripeAddress,
  pollMembershipPaymentResult,
  buildConfirmPaymentParams,
  buildCustomerInfo,
  runAgentPreConfirmPayment,
  buildMembershipMetadata,
  runStripePreSubmitValidation,
  initiateSetupIntentForFreeCheckout,
  syncPaymentIntent,
};
